import { FilterRange } from '@common/types';
import { addDays } from 'date-fns';
import { action, computed, observable } from 'mobx';

import { FilterStore, SortStore } from '~/@store/common';
import { ProjectTimeSpentRecord } from '~/@views/UserView/ProjectPage/ProjectTimeSpent/types';
import { parseDateFrom } from '~/utils/date';
import { filterAndSortCollection, getDateRangeFilterFn, getStringSearchFilterFn } from '~/utils/filters';

const DEFAULT_PROJECT_TIME_SPENT_SORT = {
    field: 'date',
    direction: -1,
};

const DEFAULT_PROJECT_TIME_SPENT_DATE_FILTER: FilterRange<Date> = [addDays(new Date(), -7), null];

export type ProjectTimeSpentStoreFilter = {
    userName?: string | null;
    dumpLoadNumber?: string | null;
    date?: FilterRange<Date> | null;
    endDate?: FilterRange<Date> | null;
};

export enum ProjectTimeSpentFilterField {
    userName = 'userName',
    dumpLoadNumber = 'dumpLoadNumber',
    date = 'date',
    endDate = 'endDate',
    licensePlate = 'licensePlate',
    vehicleTypeName = 'vehicleTypeName',
    hoursSpent = 'hoursSpent',
    fuel = 'fuel',
    engine = 'engine',
    co2 = 'co2',
    comment = 'comment',
}

class ProjectTimeSpentStore {
    filter = new FilterStore<ProjectTimeSpentStoreFilter>();
    sort = new SortStore(DEFAULT_PROJECT_TIME_SPENT_SORT);

    @observable.shallow
    list: ProjectTimeSpentRecord[] = [];

    @action
    setList(newList: ProjectTimeSpentRecord[]) {
        this.list = newList;
        this.filter.values.date = DEFAULT_PROJECT_TIME_SPENT_DATE_FILTER;
    }

    @action
    clear() {
        this.filter.clearValues();
        this.sort.clearSort();
    }

    @action setDateFilterValue(value: FilterRange<Date> | null) {
        const newValue = value && (value[0] || value[1]) ? value : DEFAULT_PROJECT_TIME_SPENT_DATE_FILTER;
        this.filter.setValue(ProjectTimeSpentFilterField.date, newValue);
    }

    @action setEndDateFilterValue(value: FilterRange<Date> | null) {
        const newValue = value && (value[0] || value[1]) ? value : null;
        this.filter.setValue(ProjectTimeSpentFilterField.endDate, newValue);
    }

    @computed
    get filteredList(): ProjectTimeSpentRecord[] {
        const { userName, dumpLoadNumber, date, endDate } = this.filter.values;

        const filters: ((item: ProjectTimeSpentRecord) => boolean)[] = [];

        if (dumpLoadNumber) filters.push(getStringSearchFilterFn(dumpLoadNumber, r => r.dumpLoadNumber));
        if (date) filters.push(getDateRangeFilterFn(date, r => parseDateFrom(r.date)));
        if (endDate) filters.push(getDateRangeFilterFn(endDate, r => parseDateFrom(r.endDate)));
        if (userName) filters.push(getStringSearchFilterFn(userName, r => r.userName));

        return filterAndSortCollection(this.list, filters, this.sort.value);
    }
}

export const projectTimeSpentStore = new ProjectTimeSpentStore();
